import React, { useState, useEffect } from 'react';
import {
  FileText, FileCheck, Shield, Building2, CheckCircle2,
  XCircle, Clock, AlertTriangle, Search, Plus, Upload,
  ChevronRight, Eye, Percent, Calendar, Users, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import AEDIcon from '@/components/icons/AEDIcon';
import DeadlineAdminPanel from '@/components/DeadlineAdminPanel';
import { toast } from 'sonner';
import api from '@/services/api';

const STATUS_COLORS = {
  DRAFT: 'bg-gray-600/20 text-gray-400',
  PENDING_REVIEW: 'bg-yellow-600/20 text-yellow-400',
  VERIFIED: 'bg-blue-600/20 text-blue-400',
  SIGNED: 'bg-green-600/20 text-green-400',
  EXPIRED: 'bg-red-600/20 text-red-400',
  CANCELLED: 'bg-red-600/20 text-red-400',
};

const MILESTONE_STATUS = {
  NOT_STARTED: { color: 'text-gray-400', icon: Clock },
  IN_PROGRESS: { color: 'text-yellow-400', icon: RefreshCw },
  COMPLETED: { color: 'text-green-400', icon: CheckCircle2 },
  BLOCKED: { color: 'text-red-400', icon: XCircle },
  OVERDUE: { color: 'text-red-400', icon: AlertTriangle },
};

const formatCurrency = (amount) => {
  if (!amount) return 'AED 0';
  return `AED ${amount.toLocaleString()}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-AE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// ============ TRANSFER READINESS COMPONENT ============

const TransferReadinessCard = ({ dealId }) => {
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReadiness();
  }, [dealId]);

  const fetchReadiness = async () => {
    try {
      const response = await api.get(`/contracts/deal/${dealId}/readiness`);
      setReadiness(response.data.readiness);
    } catch (error) {
      console.error('Error fetching readiness:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Loading...</div>;
  if (!readiness) return <div className="text-center py-8 text-muted-foreground">No readiness data</div>;

  const milestones = [
    { key: 'form_a_status', label: 'Form A (Seller-Broker)', status: readiness.form_a_status },
    { key: 'form_b_status', label: 'Form B (Buyer-Broker)', status: readiness.form_b_status },
    { key: 'form_f_status', label: 'Form F (MOU)', status: readiness.form_f_status },
    { key: 'noc_status', label: 'NOC (Developer)', status: readiness.noc_status },
  ];

  const financials = [
    { label: 'Deposit Paid', completed: readiness.deposit_paid },
    { label: 'Service Charges Cleared', completed: readiness.service_charges_cleared },
    { label: 'Mortgage Approved', completed: readiness.mortgage_approved },
    { label: 'Trustee Booked', completed: readiness.trustee_booked },
  ];

  return (
    <Card className="glass-card border-white/10">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          Transfer Readiness
        </CardTitle>
        <CardDescription>Deal: {dealId}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Progress Bar */}
        <div>
          <div className="flex justify-between mb-2">
            <span className="text-sm text-muted-foreground">Overall Progress</span>
            <span className="text-sm font-medium text-primary">{readiness.overall_readiness_percentage}%</span>
          </div>
          <Progress value={readiness.overall_readiness_percentage} className="h-2" />
        </div>

        {/* Document Status */}
        <div>
          <h4 className="text-sm font-medium mb-3">Document Status</h4>
          <div className="grid grid-cols-2 gap-3">
            {milestones.map((m) => {
              const statusConfig = MILESTONE_STATUS[m.status] || MILESTONE_STATUS.NOT_STARTED;
              const Icon = statusConfig.icon;
              return (
                <div key={m.key} className="flex items-center gap-2 p-2 rounded bg-secondary/30">
                  <Icon className={`w-4 h-4 ${statusConfig.color}`} />
                  <span className="text-xs">{m.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Financial Status */}
        <div>
          <h4 className="text-sm font-medium mb-3">Financial & Coordination</h4>
          <div className="grid grid-cols-2 gap-3">
            {financials.map((f) => (
              <div key={f.label} className="flex items-center gap-2 p-2 rounded bg-secondary/30">
                {f.completed ? (
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                ) : (
                  <Clock className="w-4 h-4 text-gray-400" />
                )}
                <span className="text-xs">{f.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Blocking Items */}
        {readiness.blocking_items.length > 0 && (
          <div className="p-3 rounded bg-red-600/10 border border-red-600/30">
            <h4 className="text-sm font-medium text-red-400 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Blocking Items ({readiness.blocking_items.length})
            </h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              {readiness.blocking_items.slice(0, 5).map((item, idx) => (
                <li key={idx}>• {item}</li>
              ))}
              {readiness.blocking_items.length > 5 && (
                <li className="text-red-400">...and {readiness.blocking_items.length - 5} more</li>
              )}
            </ul>
          </div>
        )}

        {/* Trustee Info */}
        {readiness.trustee_booked && (
          <div className="p-3 rounded bg-green-600/10 border border-green-600/30">
            <div className="flex items-center gap-2 text-green-400">
              <Calendar className="w-4 h-4" />
              <span className="text-sm font-medium">Trustee Appointment</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {formatDate(readiness.trustee_appointment_date)}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============ CONTRACT CARD COMPONENT ============

const ContractCard = ({ contract, type, onRefresh }) => {
  if (!contract) {
    return (
      <Card className="glass-card border-white/10 border-dashed">
        <CardContent className="py-8 text-center">
          <FileText className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{type} not created</p>
        </CardContent>
      </Card>
    );
  }

  const statusColor = STATUS_COLORS[contract.status] || STATUS_COLORS.DRAFT;

  return (
    <Card className="glass-card border-white/10">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" />
            {type}
          </CardTitle>
          <Badge className={statusColor}>{contract.status}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-xs text-muted-foreground">
          <p>ID: {contract.id}</p>
          <p>Created: {formatDate(contract.created_at)}</p>
        </div>

        {/* Form F specific info */}
        {type === 'Form F' && contract.financial_terms && (
          <div className="p-2 rounded bg-secondary/30">
            <div className="flex items-center gap-2 mb-1">
              <AEDIcon className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">{formatCurrency(contract.financial_terms.sale_price)}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Deposit: {formatCurrency(contract.financial_terms.deposit_amount)}
            </p>
          </div>
        )}

        {/* Signatures for Form F */}
        {type === 'Form F' && (
          <div className="flex gap-2">
            <div className={`flex-1 p-2 rounded text-center ${contract.seller_signed ? 'bg-green-600/20' : 'bg-secondary/30'}`}>
              <p className="text-xs">{contract.seller_signed ? '✓ Seller Signed' : 'Seller Pending'}</p>
            </div>
            <div className={`flex-1 p-2 rounded text-center ${contract.buyer_signed ? 'bg-green-600/20' : 'bg-secondary/30'}`}>
              <p className="text-xs">{contract.buyer_signed ? '✓ Buyer Signed' : 'Buyer Pending'}</p>
            </div>
          </div>
        )}

        {/* NOC specific */}
        {type === 'NOC' && (
          <div className="space-y-2">
            <p className="text-xs"><strong>Developer:</strong> {contract.developer_name}</p>
            {contract.outstanding_service_charges > 0 && (
              <p className="text-xs text-yellow-400">
                Outstanding: {formatCurrency(contract.outstanding_service_charges)}
              </p>
            )}
            {contract.actual_approval_date && (
              <p className="text-xs text-green-400">
                Approved: {formatDate(contract.actual_approval_date)}
              </p>
            )}
          </div>
        )}

        {/* Documents count */}
        {contract.documents && contract.documents.length > 0 && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Upload className="w-3 h-3" />
            {contract.documents.length} document(s) attached
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============ CREATE FORM F DIALOG ============

const CreateFormFDialog = ({ dealId, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    deal_id: dealId || '',
    seller: { name: '', emirates_id: '', phone: '', email: '' },
    buyer: { name: '', passport_number: '', phone: '', email: '' },
    property_details: {
      title_deed_number: '',
      building_name: '',
      unit_number: '',
      area_name: '',
      property_type: 'apartment',
      size_sqft: ''
    },
    financial_terms: {
      sale_price: '',
      deposit_percentage: 10,
      deposit_amount: '',
      commission_percentage: 2,
      payment_method: 'CASH'
    },
    dates: {
      signing_date: '',
      completion_date: '',
      deposit_due_date: '',
      noc_submission_deadline: '',
      trustee_appointment_date: ''
    }
  });

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Calculate deposit if not set
      const payload = { ...formData };
      if (!payload.financial_terms.deposit_amount && payload.financial_terms.sale_price) {
        payload.financial_terms.deposit_amount = 
          payload.financial_terms.sale_price * (payload.financial_terms.deposit_percentage / 100);
      }

      await api.post('/contracts/form-f', payload);
      toast.success('Form F created successfully');
      setOpen(false);
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || 'Failed to create Form F');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-gold-metallic">
          <Plus className="w-4 h-4 mr-2" /> Create Form F
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto glass-card">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Create Form F (Buyer-Seller MOU)
          </DialogTitle>
          <DialogDescription>
            Dubai secondary market transaction agreement
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="parties" className="w-full">
          <TabsList className="grid grid-cols-4 mb-4">
            <TabsTrigger value="parties">Parties</TabsTrigger>
            <TabsTrigger value="property">Property</TabsTrigger>
            <TabsTrigger value="financial">Financial</TabsTrigger>
            <TabsTrigger value="dates">Dates</TabsTrigger>
          </TabsList>

          <TabsContent value="parties" className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Deal ID</Label>
              <Input
                value={formData.deal_id}
                onChange={(e) => setFormData(prev => ({ ...prev, deal_id: e.target.value }))}
                placeholder="deal-001"
                className="bg-transparent border-white/20"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-primary">Seller</h4>
                <Input
                  placeholder="Full Name"
                  value={formData.seller.name}
                  onChange={(e) => updateField('seller', 'name', e.target.value)}
                  className="bg-transparent border-white/20"
                />
                <Input
                  placeholder="Emirates ID"
                  value={formData.seller.emirates_id}
                  onChange={(e) => updateField('seller', 'emirates_id', e.target.value)}
                  className="bg-transparent border-white/20"
                />
                <Input
                  placeholder="Phone"
                  value={formData.seller.phone}
                  onChange={(e) => updateField('seller', 'phone', e.target.value)}
                  className="bg-transparent border-white/20"
                />
                <Input
                  placeholder="Email"
                  value={formData.seller.email}
                  onChange={(e) => updateField('seller', 'email', e.target.value)}
                  className="bg-transparent border-white/20"
                />
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-medium text-purple-400">Buyer</h4>
                <Input
                  placeholder="Full Name"
                  value={formData.buyer.name}
                  onChange={(e) => updateField('buyer', 'name', e.target.value)}
                  className="bg-transparent border-white/20"
                />
                <Input
                  placeholder="Passport Number"
                  value={formData.buyer.passport_number}
                  onChange={(e) => updateField('buyer', 'passport_number', e.target.value)}
                  className="bg-transparent border-white/20"
                />
                <Input
                  placeholder="Phone"
                  value={formData.buyer.phone}
                  onChange={(e) => updateField('buyer', 'phone', e.target.value)}
                  className="bg-transparent border-white/20"
                />
                <Input
                  placeholder="Email"
                  value={formData.buyer.email}
                  onChange={(e) => updateField('buyer', 'email', e.target.value)}
                  className="bg-transparent border-white/20"
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="property" className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Title Deed Number</Label>
                <Input
                  value={formData.property_details.title_deed_number}
                  onChange={(e) => updateField('property_details', 'title_deed_number', e.target.value)}
                  className="bg-transparent border-white/20"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Building Name</Label>
                <Input
                  value={formData.property_details.building_name}
                  onChange={(e) => updateField('property_details', 'building_name', e.target.value)}
                  className="bg-transparent border-white/20"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Unit Number</Label>
                <Input
                  value={formData.property_details.unit_number}
                  onChange={(e) => updateField('property_details', 'unit_number', e.target.value)}
                  className="bg-transparent border-white/20"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Area</Label>
                <Input
                  value={formData.property_details.area_name}
                  onChange={(e) => updateField('property_details', 'area_name', e.target.value)}
                  placeholder="Dubai Marina"
                  className="bg-transparent border-white/20"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Property Type</Label>
                <Select
                  value={formData.property_details.property_type}
                  onValueChange={(v) => updateField('property_details', 'property_type', v)}
                >
                  <SelectTrigger className="bg-transparent border-white/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apartment">Apartment</SelectItem>
                    <SelectItem value="villa">Villa</SelectItem>
                    <SelectItem value="townhouse">Townhouse</SelectItem>
                    <SelectItem value="penthouse">Penthouse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Size (sq ft)</Label>
                <Input
                  type="number"
                  value={formData.property_details.size_sqft}
                  onChange={(e) => updateField('property_details', 'size_sqft', parseFloat(e.target.value))}
                  className="bg-transparent border-white/20"
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="financial" className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <AEDIcon className="w-3 h-3" /> Sale Price
                </Label>
                <Input
                  type="number"
                  value={formData.financial_terms.sale_price}
                  onChange={(e) => updateField('financial_terms', 'sale_price', parseFloat(e.target.value))}
                  placeholder="2,500,000"
                  className="bg-transparent border-white/20"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Deposit %</Label>
                <Input
                  type="number"
                  value={formData.financial_terms.deposit_percentage}
                  onChange={(e) => updateField('financial_terms', 'deposit_percentage', parseFloat(e.target.value))}
                  className="bg-transparent border-white/20"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Commission %</Label>
                <Input
                  type="number"
                  value={formData.financial_terms.commission_percentage}
                  onChange={(e) => updateField('financial_terms', 'commission_percentage', parseFloat(e.target.value))}
                  className="bg-transparent border-white/20"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Payment Method</Label>
                <Select
                  value={formData.financial_terms.payment_method}
                  onValueChange={(v) => updateField('financial_terms', 'payment_method', v)}
                >
                  <SelectTrigger className="bg-transparent border-white/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CASH">Cash</SelectItem>
                    <SelectItem value="MORTGAGE">Mortgage</SelectItem>
                    <SelectItem value="PAYMENT_PLAN">Payment Plan</SelectItem>
                    <SelectItem value="MIXED">Mixed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.financial_terms.sale_price > 0 && (
              <div className="p-3 rounded bg-primary/10 border border-primary/30">
                <p className="text-sm">
                  <strong>Calculated Deposit:</strong> {formatCurrency(
                    formData.financial_terms.sale_price * (formData.financial_terms.deposit_percentage / 100)
                  )}
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="dates" className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Signing Date</Label>
                <Input
                  type="date"
                  value={formData.dates.signing_date}
                  onChange={(e) => updateField('dates', 'signing_date', e.target.value)}
                  className="bg-transparent border-white/20"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Completion Date</Label>
                <Input
                  type="date"
                  value={formData.dates.completion_date}
                  onChange={(e) => updateField('dates', 'completion_date', e.target.value)}
                  className="bg-transparent border-white/20"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Deposit Due Date</Label>
                <Input
                  type="date"
                  value={formData.dates.deposit_due_date}
                  onChange={(e) => updateField('dates', 'deposit_due_date', e.target.value)}
                  className="bg-transparent border-white/20"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">NOC Deadline</Label>
                <Input
                  type="date"
                  value={formData.dates.noc_submission_deadline}
                  onChange={(e) => updateField('dates', 'noc_submission_deadline', e.target.value)}
                  className="bg-transparent border-white/20"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Trustee Appointment</Label>
                <Input
                  type="date"
                  value={formData.dates.trustee_appointment_date}
                  onChange={(e) => updateField('dates', 'trustee_appointment_date', e.target.value)}
                  className="bg-transparent border-white/20"
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="btn-gold-metallic">
            {loading ? 'Creating...' : 'Create Form F'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ MAIN PANEL ============

const ContractAdminPanel = () => {
  const [searchDealId, setSearchDealId] = useState('');
  const [contracts, setContracts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recentDeals, setRecentDeals] = useState([]);

  // Fetch recent deals with contracts
  useEffect(() => {
    fetchRecentContracts();
  }, []);

  const fetchRecentContracts = async () => {
    try {
      // In a real implementation, this would fetch from a dedicated endpoint
      // For now, we'll show manual search
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const searchContracts = async () => {
    if (!searchDealId.trim()) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/contracts/deal/${searchDealId}`);
      setContracts(response.data);
    } catch (error) {
      toast.error('Failed to fetch contracts');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (searchDealId) searchContracts();
  };

  return (
    <div className="space-y-6" data-testid="contract-admin-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-display flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary" />
            Contract Intelligence
          </h2>
          <p className="text-sm text-muted-foreground">
            Dubai secondary market: Form A, B, F + NOC
          </p>
        </div>
        <CreateFormFDialog onSuccess={handleRefresh} dealId={searchDealId} />
      </div>

      {/* Search */}
      <Card className="glass-card border-white/10">
        <CardContent className="py-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                placeholder="Enter Deal ID to view contracts..."
                value={searchDealId}
                onChange={(e) => setSearchDealId(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchContracts()}
                className="bg-transparent border-white/20"
              />
            </div>
            <Button onClick={searchContracts} disabled={loading}>
              <Search className="w-4 h-4 mr-2" />
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {contracts && (
        <div className="space-y-6">
          {/* Contracts Row */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left: Contracts */}
            <div className="lg:col-span-2 space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">
                Contracts for Deal: {contracts.deal_id}
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <ContractCard contract={contracts.form_a} type="Form A" onRefresh={handleRefresh} />
                <ContractCard contract={contracts.form_b} type="Form B" onRefresh={handleRefresh} />
                <ContractCard contract={contracts.form_f} type="Form F" onRefresh={handleRefresh} />
                <ContractCard contract={contracts.noc} type="NOC" onRefresh={handleRefresh} />
              </div>
            </div>

            {/* Right: Transfer Readiness */}
            <div>
              <TransferReadinessCard dealId={contracts.deal_id} />
            </div>
          </div>

          {/* Governance Deadlines Section */}
          <div className="border-t border-white/10 pt-6">
            <DeadlineAdminPanel dealId={contracts.deal_id} showStats={true} />
          </div>
        </div>
      )}

      {/* Empty State */}
      {!contracts && !loading && (
        <Card className="glass-card border-white/10">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium mb-2">No Deal Selected</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Enter a Deal ID above to view and manage contracts
            </p>
            <p className="text-xs text-muted-foreground">
              Or create a new Form F to start a transaction
            </p>
          </CardContent>
        </Card>
      )}

      {/* Transaction Flow Info */}
      <Card className="glass-card border-white/10">
        <CardHeader>
          <CardTitle className="text-sm">Dubai Secondary Transaction Flow</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-xs">
            <div className="flex items-center gap-1 px-3 py-2 rounded bg-blue-600/20 text-blue-400">
              <FileText className="w-3 h-3" /> Form A
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
            <div className="flex items-center gap-1 px-3 py-2 rounded bg-purple-600/20 text-purple-400">
              <FileText className="w-3 h-3" /> Form B
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
            <div className="flex items-center gap-1 px-3 py-2 rounded bg-primary/20 text-primary">
              <FileCheck className="w-3 h-3" /> Form F
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
            <div className="flex items-center gap-1 px-3 py-2 rounded bg-green-600/20 text-green-400">
              <Shield className="w-3 h-3" /> NOC
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
            <div className="flex items-center gap-1 px-3 py-2 rounded bg-emerald-600/20 text-emerald-400">
              <Building2 className="w-3 h-3" /> Transfer
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContractAdminPanel;
